import { createFeature, createReducer, on } from '@ngrx/store';

import { WeeklySchedule } from '@core/models';
import { emptyWeek } from '@core/util/weekly-schedule.util';
import { ScheduleApiActions, SchedulePageActions } from './schedule.actions';

/**
 * `schedule` feature slice — one weekly schedule per persona, keyed by code.
 *
 * Edits land on the schedule itself rather than on a parallel overlay. There used
 * to be two shapes for one thing: a read-only bundle of everyone's weeks, and a
 * `ScheduleEdits` map of `"7:05 AM|mon"` → replacement cell that was never
 * persisted. One of them was never meant to be written, which is why nothing ever
 * was.
 */
export interface ScheduleState {
  readonly schedules: Record<string, WeeklySchedule>;
  /**
   * Persona codes with a week, from the portfolio list. Null until it is read —
   * which is not the same as "nobody has one", and the card has to tell them
   * apart or everyone reads as NOT YET while the call is in flight.
   */
  readonly authoredCodes: string[] | null;
  readonly loading: boolean;
  readonly error: string | null;
}

const initialState: ScheduleState = { schedules: {}, authoredCodes: null, loading: false, error: null };

/** The persona's week, created EMPTY the first time it is touched. */
function weekFor(state: ScheduleState, personaCode: string): WeeklySchedule {
  return state.schedules[personaCode] ?? emptyWeek(personaCode);
}

/** Replace a persona's week, marking it authored — any edit counts as authoring. */
function withWeek(state: ScheduleState, week: WeeklySchedule): ScheduleState {
  return { ...state, schedules: { ...state.schedules, [week.personaCode]: { ...week, authored: true } } };
}

/** Upsert by identity, keeping position on update and appending on create. */
function upsertBy<T>(list: readonly T[], row: T, same: (candidate: T) => boolean): T[] {
  return list.some(same) ? list.map((x) => (same(x) ? row : x)) : [...list, row];
}

export const scheduleFeature = createFeature({
  name: 'schedule',
  reducer: createReducer(
    initialState,
    on(SchedulePageActions.entered, (state) => ({ ...state, loading: true, error: null })),
    on(ScheduleApiActions.loadSuccess, (state, { schedules }) => ({
      ...state,
      schedules: Object.fromEntries(schedules.map((s) => [s.personaCode, s])),
      loading: false,
      error: null,
    })),
    on(ScheduleApiActions.loadFailure, (state, { error }) => ({ ...state, loading: false, error })),
    on(ScheduleApiActions.portfolioLoaded, (state, { personaCodes }) => ({ ...state, authoredCodes: personaCodes })),

    // The server's copy, after a write — replaces whatever the optimistic apply left.
    on(ScheduleApiActions.weekLoaded, (state, { schedule }) => ({
      ...state,
      schedules: { ...state.schedules, [schedule.personaCode]: schedule },
    })),
    // An overwrite is one operation: what it covers goes, the new one lands.
    // Splitting it into a remove then an add would leave the week briefly wrong
    // and, once this is wired, half-written if the second call failed.
    on(SchedulePageActions.activitiesSaved, (state, { personaCode, removed, activity }) => {
      const week = weekFor(state, personaCode);
      const kept = week.activities.filter((a) => !removed.includes(a.activityGuid));
      return withWeek(state, { ...week, activities: upsertBy(kept, activity, (x) => x.activityGuid === activity.activityGuid) });
    }),
    on(SchedulePageActions.activityRemoved, (state, { personaCode, activityGuid }) => {
      const week = weekFor(state, personaCode);
      return withWeek(state, { ...week, activities: week.activities.filter((a) => a.activityGuid !== activityGuid) });
    }),
    on(SchedulePageActions.categorySaved, (state, { personaCode, category }) => {
      const week = weekFor(state, personaCode);
      return withWeek(state, { ...week, categories: upsertBy(week.categories, category, (x) => x.categoryGuid === category.categoryGuid) });
    }),
    on(SchedulePageActions.categoryRemoved, (state, { personaCode, categoryGuid }) => {
      const week = weekFor(state, personaCode);
      return withWeek(state, { ...week, categories: week.categories.filter((c) => c.categoryGuid !== categoryGuid) });
    }),
  ),
});

export const { selectSchedules, selectAuthoredCodes } = scheduleFeature;
